<template>
  <label :class="tileClasses" :data-cy="dataCy">
    <input
      type="radio"
      :name="groupName"
      :value="value"
      :checked="selected"
      class="mt-1 accent-blue-600"
      @change="$emit('select', value)"
    />
    <div class="flex-1 min-w-0">
      <div v-if="kind === 'card'" class="flex items-center gap-2 flex-wrap">
        <span :class="brandPillClasses">{{ brandLabel }}</span>
        <span
          class="font-mono text-sm tabular-nums tracking-wide text-gray-900"
        >
          •••• {{ cardLast4 }}
        </span>
        <span class="text-xs text-gray-500">{{ payerName }}</span>
      </div>
      <div v-else class="flex items-center gap-2 flex-wrap">
        <span class="text-sm font-medium text-gray-900"
          >Cash or check refund</span
        >
        <span class="text-xs text-gray-500"
          >— not tied to a card transaction</span
        >
      </div>

      <div
        class="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-600"
      >
        <span v-if="chargedLabel">
          Charged
          <strong class="font-mono tabular-nums text-gray-900 font-medium">{{
            chargedLabel
          }}</strong>
        </span>
        <span v-if="originalCents !== undefined">
          Original
          <strong class="font-mono tabular-nums text-gray-900 font-medium">{{
            fmt(originalCents)
          }}</strong>
        </span>
        <span>
          Refundable
          <strong class="font-mono tabular-nums text-gray-900 font-medium">{{
            fmt(refundableCents)
          }}</strong>
        </span>
      </div>

      <div v-if="showProgress" class="mt-2">
        <div class="h-1 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            class="h-full bg-red-500/60"
            :style="{ width: `${progressPercent}%` }"
          />
        </div>
        <div class="mt-1 text-xs text-gray-500">
          {{ progressLabel }}
        </div>
      </div>
    </div>
  </label>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { dollars } from "@/mixins";

const props = withDefaults(
  defineProps<{
    value: string;
    selected: boolean;
    kind: "card" | "cash";
    groupName?: string;
    refundableCents: number;
    originalCents?: number;
    alreadyRefundedCents?: number;
    cardBrand?: string;
    cardLast4?: string;
    payerName?: string;
    chargedLabel?: string;
    dataCy?: string;
  }>(),
  {
    groupName: "refund-source",
    originalCents: undefined,
    alreadyRefundedCents: 0,
    cardBrand: "",
    cardLast4: "",
    payerName: "",
    chargedLabel: "",
    dataCy: undefined
  }
);

defineEmits<{
  (e: "select", value: string): void;
}>();

const tileClasses = computed(() => {
  const base =
    "flex items-start gap-3 rounded-lg border px-4 py-3 transition-colors duration-150 cursor-pointer";
  return props.selected
    ? `${base} border-blue-500 bg-blue-50 ring-1 ring-blue-500`
    : `${base} border-gray-200 bg-white hover:border-gray-400`;
});

const brandLabel = computed(() => {
  const b = (props.cardBrand || "").toLowerCase();
  if (b.includes("master")) return "MC";
  if (b.includes("amex") || b.includes("american")) return "AMEX";
  if (b.includes("visa")) return "VISA";
  if (b.includes("discover")) return "DISC";
  return props.cardBrand?.toUpperCase() || "CARD";
});

const brandPillClasses = computed(() => {
  const base =
    "inline-flex items-center font-mono text-[10px] font-semibold tracking-wider text-white px-1.5 py-0.5 rounded";
  const b = brandLabel.value;
  if (b === "MC") return `${base} bg-red-600`;
  if (b === "AMEX") return `${base} bg-blue-700`;
  if (b === "VISA") return `${base} bg-gray-900`;
  if (b === "DISC") return `${base} bg-orange-500`;
  return `${base} bg-gray-700`;
});

const showProgress = computed(
  () =>
    props.kind === "card" &&
    (props.originalCents ?? 0) > 0 &&
    (props.alreadyRefundedCents ?? 0) > 0
);

const progressPercent = computed(() => {
  const original = props.originalCents ?? 0;
  if (original <= 0) return 0;
  const already = props.alreadyRefundedCents ?? 0;
  return Math.min(100, Math.round((already / original) * 100));
});

const progressLabel = computed(() => {
  const already = props.alreadyRefundedCents ?? 0;
  if (already <= 0) return `${fmt(0)} already refunded`;
  return `${fmt(already)} already refunded`;
});

function fmt(cents: number): string {
  return dollars(cents);
}
</script>
